import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChefHat, PaperPlaneTilt, X, SpinnerGap, ArrowRight, Microphone, Stop, ClockCounterClockwise, Plus, Trash, ChatCircleDots, Lightning, Check, FileText, Flask, CurrencyCircleDollar, CheckSquare, Package, ThumbsUp, ThumbsDown, Brain, Gear, FileXls, Download } from "@phosphor-icons/react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import ExportTemplateConfig from "@/components/ExportTemplateConfig";
import { downloadBase64File } from "@/utils/downloadHelper";
import {
  getSuggestions, parseRecipeBlock, removeJsonBlock, parseActionBlock, removeActionBlock,
  parseChecklistBlock, removeChecklistBlock, parsePriceUpdateBlock, removePriceUpdateBlock,
  parseNewIngredientBlock, parseAllNewIngredientBlocks, removeNewIngredientBlock,
  parseIngredientUpdateBlock, parseIngredientMergeBlock, parseIngredientDeleteBlock,
  parseExportBlock, removeAllAIBlocks, formatMessage, findMatchingIngredient
} from "@/components/AiBlockParsers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Draft badge component
function DraftBadge() {
  return (
    <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mb-1.5">
      <Flask size={12} className="text-amber-600" weight="fill" />
      <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">Rascunho de Revisão</span>
    </div>
  );
}

export default function SousChefAI() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [sessionId] = useState(() => `souschef-${Date.now()}`);
  const [conversationId, setConversationId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [feedbackGiven, setFeedbackGiven] = useState({}); // {msgId: "positive"|"negative"}
  const [showTemplateConfig, setShowTemplateConfig] = useState(false);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const streamAbortRef = useRef(null);

  useEffect(() => () => { if (streamAbortRef.current) streamAbortRef.current.abort(); }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const scrollToBottom = useCallback(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, []);
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => { if (isOpen && inputRef.current) inputRef.current.focus(); }, [isOpen]);

  // ==================== HISTORY ====================
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/sous-chef/history`);
      setHistory(data);
    } catch { /* */ }
    finally { setHistoryLoading(false); }
  };

  const loadConversation = async (convId) => {
    try {
      const { data } = await axios.get(`${API}/sous-chef/history/${convId}`);
      setMessages((data.messages || []).map((m, i) => ({ id: `hist-${i}`, role: m.role, content: m.content })));
      setConversationId(convId);
      setShowHistory(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao carregar conversa. Tente novamente.");
    }
  };

  const deleteConversation = async (convId) => {
    try {
      await axios.delete(`${API}/sous-chef/history/${convId}`);
      setHistory(h => h.filter(c => c.conversation_id !== convId));
    } catch { /* */ }
  };

  const startNewConversation = () => {
    setMessages([]);
    setConversationId(null);
    setShowHistory(false);
  };

  // ==================== VOICE ====================
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        await transcribeAudio(blob);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch {
      toast.error("Não foi possível acessar o microfone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    clearInterval(recordingTimerRef.current);
  };

  const transcribeAudio = async (blob) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", blob, "voice.webm");
      const token = localStorage.getItem("access_token");
      const { data } = await axios.post(`${API}/sous-chef/voice`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });
      if (data.text) {
        setInput(data.text);
        setIsLoading(false);
      }
    } catch {
      toast.error("Erro na transcrição de voz");
      setIsLoading(false);
    }
  };

  // ==================== SEND ====================
  const fetchChatWithRetry = async (body, token) => {
    const attempt = async (timeoutMs) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        return await fetch(`${API}/sous-chef/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timer);
      }
    };
    try {
      return await attempt(120000);
    } catch (e) {
      // Retry once on fast network failure (Safari "Load failed" / "Failed to fetch")
      if (e.name !== "AbortError") {
        await new Promise((r) => setTimeout(r, 1500));
        return await attempt(120000);
      }
      throw e;
    }
  };

  const friendlyError = (error) => {
    if (error.name === "AbortError") return "A resposta demorou demais. Tente uma pergunta mais curta ou tente novamente.";
    const m = String(error.message || "");
    if (/load failed|failed to fetch|networkerror|network request failed/i.test(m))
      return "Falha de conexão com o servidor. Verifique sua internet e tente novamente.";
    return m || "Erro inesperado. Tente novamente.";
  };

  const handleSend = async (text) => {
    const msg = text || input.trim();
    if (!msg || isLoading) return;

    const userMsg = { id: `msg-${Date.now()}`, role: "user", content: msg };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);

    try {
      const token = localStorage.getItem("access_token");
      const assistantMsgId = `msg-${Date.now()}-ai`;
      setMessages(prev => [...prev, { id: assistantMsgId, role: "assistant", content: "..." }]);
      const body = { message: msg, current_page: location.pathname, session_id: sessionId, conversation_id: conversationId };

      // Etapa 11: streaming SSE — tokens em tempo real, com fallback para o endpoint clássico
      let streamed = false;
      try {
        if (streamAbortRef.current) streamAbortRef.current.abort();
        const controller = new AbortController();
        streamAbortRef.current = controller;
        const streamResp = await fetch(`${API}/sous-chef/chat/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        if (streamResp.ok && streamResp.body) {
          const reader = streamResp.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let acc = "";
          let finalData = null;
          let streamError = null;
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split("\n\n");
            buffer = parts.pop();
            for (const part of parts) {
              if (!part.startsWith("data: ")) continue;
              let evt;
              try { evt = JSON.parse(part.slice(6)); } catch { continue; }
              if (evt.error) { streamError = evt.error; }
              if (evt.delta) {
                acc += evt.delta;
                const current = acc;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { id: assistantMsgId, role: "assistant", content: current };
                  return updated;
                });
              }
              if (evt.done) finalData = evt;
            }
          }
          if (streamError && !acc) throw new Error(streamError);
          if (acc || finalData) {
            if (finalData?.conversation_id) setConversationId(finalData.conversation_id);
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { id: assistantMsgId, role: "assistant", content: acc, actions: finalData?.actions || [] };
              return updated;
            });
            streamed = true;
          }
        }
      } catch (streamErr) {
        if (streamErr?.name === "AbortError") { setIsLoading(false); return; }
        streamed = false;
      }

      if (!streamed) {
        const response = await fetchChatWithRetry(body, token);
        if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.detail || `Erro ${response.status}`); }
        const data = await response.json();
        if (data.conversation_id) setConversationId(data.conversation_id);

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { id: assistantMsgId, role: "assistant", content: data.response, actions: data.actions };
          return updated;
        });
      }
    } catch (error) {
      setMessages(prev => [...prev.slice(0, -1), { id: `msg-${Date.now()}-err`, role: "assistant", content: `⚠️ ${friendlyError(error)}`, retryText: msg }]);
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== ACTIONS ====================
  const handleExecuteAction = async (action) => {
    try {
      const { data } = await axios.post(`${API}/sous-chef/execute-action`, action);
      if (data.success) {
        toast.success(data.message);
        if (data.navigate) navigate(data.navigate);
      } else {
        toast.error(data.message || "Ação falhou");
      }
    } catch { toast.error("Erro ao executar ação"); }
  };

  // Draft approval: send "Aprovado" to the AI so it generates the JSON
  const handleApproveDraft = () => {
    handleSend("Aprovado. Pode criar a ficha técnica.");
  };

  // Price search
  const [priceSearching, setPriceSearching] = useState(false);
  const [priceResults, setPriceResults] = useState(null);

  const handleSearchPrices = async () => {
    setPriceSearching(true);
    try {
      const { data } = await axios.post(`${API}/sous-chef/search-prices`, { ingredient_ids: [] });
      setPriceResults(data);
      const summary = data.prices.map(p => 
        `**${p.name}**: ${data.symbol} ${p.current_price.toFixed(2)} → ${data.symbol} ${p.estimated_price.toFixed(2)} (${p.change_pct > 0 ? '+' : ''}${p.change_pct.toFixed(1)}%)`
      ).join("\n");
      setMessages(prev => [...prev, {
        id: `msg-${Date.now()}-prices`,
        role: "assistant",
        content: `## Pesquisa de Preços — ${data.location}\n\n${summary}\n\nDeseja atualizar os preços?`,
        priceData: data,
      }]);
    } catch (e) {
      toast.error("Erro na pesquisa de preços");
    } finally { setPriceSearching(false); }
  };

  const handleApplyPrices = async (priceData) => {
    try {
      const updates = priceData.prices
        .filter(p => p.estimated_price > 0 && p.estimated_price !== p.current_price)
        .map(p => ({ ingredient_id: p.ingredient_id, new_price: p.estimated_price }));
      if (updates.length === 0) { toast.info("Nenhum preço para atualizar"); return; }
      const { data } = await axios.post(`${API}/ingredients/bulk-update-prices`, { updates });
      toast.success(data.message);
      setPriceResults(null);
      setMessages(prev => [...prev, { id: `msg-${Date.now()}-ok`, role: "assistant", content: `**Preços atualizados!** ${data.message}` }]);
    } catch { toast.error("Erro ao atualizar preços"); }
  };

  // Create checklist from AI response
  const handleCreateChecklist = async (checklistData) => {
    try {
      const items = (checklistData.items || []).map(text => ({
        id: crypto.randomUUID?.() || Date.now().toString(),
        text: typeof text === 'string' ? text : text.text || '',
        checked: false,
        category: null,
      }));
      await axios.post(`${API}/checklists`, {
        name: checklistData.name,
        checklist_type: checklistData.type || "custom",
        items,
      });
      toast.success(`Checklist "${checklistData.name}" criada!`);
      setMessages(prev => [...prev, { id: `msg-${Date.now()}-cl`, role: "assistant", content: `**Checklist criada!** "${checklistData.name}" com ${items.length} itens.\n\n[Ir para Checklists](/checklists)` }]);
    } catch { toast.error("Erro ao criar checklist"); }
  };

  // Apply price updates from Sous-Chef
  const handleApplyPriceUpdate = async (priceData) => {
    try {
      const ingredientsRes = await axios.get(`${API}/ingredients`);
      const existing = ingredientsRes.data;
      const updates = [];
      for (const item of priceData.ingredients) {
        const match = findMatchingIngredient(item.name, existing);
        if (match) {
          updates.push({ ingredient_id: match.id, new_price: item.estimated_price });
        }
      }
      if (updates.length === 0) { toast.info("Nenhum ingrediente encontrado para atualizar"); return; }
      const { data } = await axios.post(`${API}/ingredients/bulk-update-prices`, { updates });
      toast.success(data.message);
      setMessages(prev => [...prev, { id: `msg-${Date.now()}-pu`, role: "assistant", content: `**Precos atualizados!** ${data.message}\n\n[Ver Ingredientes](/ingredients)` }]);
    } catch { toast.error("Erro ao atualizar precos"); }
  };

  // Create new ingredient(s) from Sous-Chef - supports single or batch
  const handleCreateNewIngredient = async (ingDataOrArray) => {
    const items = Array.isArray(ingDataOrArray) ? ingDataOrArray : [ingDataOrArray];
    try {
      const { data } = await axios.post(`${API}/sous-chef/batch-action`, {
        action_type: "ingredient_create",
        data: {
          items: items.map((i) => ({
            name: i.name,
            quantity_purchased: i.quantity_purchased || 1,
            unit: i.unit || "kg",
            purchase_price: i.purchase_price || 0,
            category: i.category || "",
            fator_coccao: i.fator_coccao || 1,
            peso_equivalente_g: i.peso_equivalente_g || null,
          })),
        },
      });
      const ops = data.operations || [];
      if (ops.length > 0) {
        toast.success(`${ops.length} insumo(s) processado(s) e fichas revinculadas!`);
        let msg = `**Cadastro em lote concluído!**\n${ops.map((o) => `- ${o}`).join("\n")}`;
        if (data.errors?.length > 0) msg += `\n\n⚠️ Falharam: ${data.errors.join(", ")}`;
        msg += `\n\n[Ver Ingredientes](/ingredients)`;
        setMessages(prev => [...prev, { id: `msg-${Date.now()}-ni`, role: "assistant", content: msg }]);
      } else {
        toast.error(data.errors?.join(", ") || "Erro ao criar ingredientes");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar ingredientes");
    }
  };

  // Execute batch ingredient update (categorize, rename, edit)
  const handleIngredientUpdate = async (updateData) => {
    try {
      const { data } = await axios.post(`${API}/sous-chef/batch-action`, {
        action_type: "ingredient_update", data: updateData,
      });
      if (data.success) {
        toast.success(`${data.operations.length} ingrediente(s) atualizado(s)!`);
        const msg = `**Atualização concluída!**\n${data.operations.map(o => `- ${o}`).join("\n")}${data.errors.length > 0 ? `\n\n⚠️ Erros: ${data.errors.join(", ")}` : ""}\n\n[Ver Ingredientes](/ingredients)`;
        setMessages(prev => [...prev, { id: `msg-${Date.now()}-iu`, role: "assistant", content: msg }]);
      } else {
        toast.error(data.errors?.[0] || "Erro na atualização");
      }
    } catch { toast.error("Erro ao atualizar ingredientes"); }
  };

  // Execute ingredient merge (deduplicate)
  const handleIngredientMerge = async (mergeData) => {
    try {
      const { data } = await axios.post(`${API}/sous-chef/batch-action`, {
        action_type: "ingredient_merge", data: mergeData,
      });
      if (data.success) {
        toast.success(`${data.operations.length} merge(s) realizado(s)!`);
        const msg = `**Duplicatas eliminadas!**\n${data.operations.map(o => `- ${o}`).join("\n")}${data.errors.length > 0 ? `\n\n⚠️ Erros: ${data.errors.join(", ")}` : ""}\n\n[Ver Ingredientes](/ingredients)`;
        setMessages(prev => [...prev, { id: `msg-${Date.now()}-im`, role: "assistant", content: msg }]);
      } else {
        toast.error(data.errors?.[0] || "Erro no merge");
      }
    } catch { toast.error("Erro ao mesclar ingredientes"); }
  };

  // Execute ingredient delete
  const handleIngredientDelete = async (deleteData) => {
    try {
      const { data } = await axios.post(`${API}/sous-chef/batch-action`, {
        action_type: "ingredient_delete", data: deleteData,
      });
      if (data.success) {
        toast.success(`${data.operations.length} ingrediente(s) deletado(s)!`);
        const msg = `**Ingredientes removidos!**\n${data.operations.map(o => `- ${o}`).join("\n")}${data.errors.length > 0 ? `\n\n⚠️ Erros: ${data.errors.join(", ")}` : ""}\n\n[Ver Ingredientes](/ingredients)`;
        setMessages(prev => [...prev, { id: `msg-${Date.now()}-id`, role: "assistant", content: msg }]);
      } else {
        toast.error(data.errors?.[0] || "Erro ao deletar");
      }
    } catch { toast.error("Erro ao deletar ingredientes"); }
  };

  // Feedback on AI responses (learning system)
  const handleFeedback = async (msgId, msgIndex, type) => {
    setFeedbackGiven(prev => ({ ...prev, [msgId]: type }));
    try {
      const reason = type === "negative" ? prompt("O que poderia melhorar? (opcional)") : null;
      await axios.post(`${API}/sous-chef/feedback`, {
        conversation_id: conversationId || "",
        message_index: msgIndex,
        feedback: type,
        reason: reason || null,
      });
      if (type === "positive") toast.success("Feedback registrado!", { duration: 1500 });
      else toast.info("Vou melhorar! Feedback salvo.", { duration: 2000 });
    } catch { /* silent */ }
  };

  const handleExportDownload = async (exportReq) => {
    try {
      const { data } = await axios.post(`${API}/export/generate`, {
        type: exportReq.type || "lista_compras",
        item_ids: exportReq.item_ids || [],
      });
      const mime = data.is_zip
        ? "application/zip"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      downloadBase64File(data.file_base64, data.file_name, mime);
      const msg = data.is_zip
        ? `ZIP com ${data.file_count} fichas baixado!`
        : "Excel baixado com sucesso!";
      toast.success(msg);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao gerar exportação");
    }
  };

  const handleApplyRecipe = async (recipe) => {
    setApplying(true);
    try {
      const ingredientsRes = await axios.get(`${API}/ingredients`);
      let existingIngredients = ingredientsRes.data;
      let createdCount = 0;
      const mappedIngredients = [];
      const newIngNames = [];

      // Identify which ingredients are new (don't exist in DB)
      const newIngs = recipe.ingredients.filter(ing => !findMatchingIngredient(ing.ingredient_name, existingIngredients));
      
      // Auto-price new ingredients via AI
      let autoPrices = {};
      if (newIngs.length > 0) {
        try {
          const { data } = await axios.post(`${API}/ingredients/auto-price`, {
            ingredients: newIngs.map(i => ({ name: i.ingredient_name, unit: i.unit || "kg", quantity_purchased: 1 }))
          });
          autoPrices = data.prices || {};
        } catch { /* silent - fallback to 0 */ }
      }

      for (const ing of recipe.ingredients) {
        const match = findMatchingIngredient(ing.ingredient_name, existingIngredients);
        if (match) {
          mappedIngredients.push({ ...ing, ingredient_id: match.id, ingredient_name: match.name });
        } else {
          // Use auto-priced value if available
          const estimatedPrice = autoPrices[ing.ingredient_name] || 0;
          const createRes = await axios.post(`${API}/ingredients/match-or-create`, {
            name: ing.ingredient_name,
            quantity_purchased: 1,
            unit: ing.unit || "kg",
            purchase_price: estimatedPrice,
            category: "",
            peso_equivalente_g: ing.peso_equivalente_g || null,
            fator_coccao_padrao: ing.fator_coccao || 1,
          });
          mappedIngredients.push({ ...ing, ingredient_id: createRes.data.id });
          existingIngredients = [...existingIngredients, createRes.data];
          createdCount++;
          newIngNames.push({ name: ing.ingredient_name, price: estimatedPrice });
        }
      }

      await axios.post(`${API}/technical-sheets`, {
        name: recipe.name,
        yield_quantity: recipe.yield_quantity || 1,
        yield_unit: recipe.yield_unit || "unidades",
        modo_preparo: recipe.modo_preparo || null,
        formula_padeiro: recipe.formula_padeiro || false,
        gramatura_porcao: recipe.gramatura_porcao || null,
        foto_url: null,
        ingrediente_base_id: null,
        ingredients: mappedIngredients.map(ing => ({
          ingredient_id: ing.ingredient_id,
          ingredient_name: ing.ingredient_name,
          quantity_used: ing.quantity_used || 0,
          quantidade_bruta: ing.quantity_used || 0,
          quantidade_liquida: (ing.quantity_used || 0) * (ing.fator_coccao || 1),
          fator_coccao: ing.fator_coccao || 1,
          unit: ing.unit || "kg",
          item_cost: 0,
          is_qb: ing.is_qb || false,
          peso_equivalente_g: ing.peso_equivalente_g || null,
          is_sub_recipe: false,
          sub_recipe_id: null,
        })),
      });

      let msg = `Ficha "${recipe.name}" criada com sucesso!`;
      if (createdCount > 0) {
        msg += ` (${createdCount} ingrediente(s) novo(s) cadastrado(s))`;
      }
      toast.success(msg);
      
      let assistantMsg = `**Ficha Tecnica registrada!** ${msg}\n\n[Ir para Fichas Tecnicas](/technical-sheets)`;
      if (newIngNames.length > 0) {
        const withPrice = newIngNames.filter(i => i.price > 0);
        const noPrice = newIngNames.filter(i => i.price === 0);
        if (withPrice.length > 0) {
          assistantMsg += `\n\n**Precos estimados pela IA** (baseado na sua regiao): ${withPrice.map(i => `${i.name} = ${i.price.toFixed(2)}`).join(", ")}. Confira em Ingredientes.`;
        }
        if (noPrice.length > 0) {
          assistantMsg += `\n\n**Atencao:** ${noPrice.length} ingrediente(s) sem preco: ${noPrice.map(i => i.name).join(", ")}. Atualize em Ingredientes ou use a Pesquisa de Precos.`;
        }
      }
      setMessages(prev => [...prev, { id: `msg-${Date.now()}-sys`, role: "assistant", content: assistantMsg }]);
    } catch (e) { toast.error(`Erro ao aplicar: ${e.response?.data?.detail || e.message}`); }
    finally { setApplying(false); }
  };

  // Check if a message looks like a draft (has structured recipe content but no JSON block)
  const isDraftMessage = (content) => {
    if (!content || typeof content !== 'string') return false;
    const hasRecipeJSON = parseRecipeBlock(content);
    if (hasRecipeJSON) return false; // Has JSON = already approved, not a draft
    const draftSignals = [
      /rascunho|draft/i,
      /rendimento.*porç/i,
      /modo de preparo|passo a passo/i,
      /perfil sensorial/i,
      /ingredientes.*:.*\n/i,
      /técnica.*aplicad/i,
    ];
    const matches = draftSignals.filter(r => r.test(content)).length;
    return matches >= 2;
  };

  // ==================== RENDER ====================
  const suggestions = getSuggestions(location.pathname);

  return (
    <>
      {/* FAB Button - positioned above bottom nav on mobile */}
      {!isOpen && (
        <button onClick={() => { setIsOpen(true); loadHistory(); }} className="fixed z-[45] w-12 h-12 md:w-14 md:h-14 rounded-full bg-zinc-900 text-white flex items-center justify-center shadow-lg hover:bg-zinc-800 transition-all hover:scale-105 bottom-20 right-4 md:bottom-6 md:right-6" data-testid="sous-chef-btn">
          <ChefHat size={24} weight="fill" className="md:hidden" />
          <ChefHat size={26} weight="fill" className="hidden md:block" />
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />
        </button>
      )}

      {/* Panel */}
      {isOpen && (
        <div className="fixed z-[90] bg-white border border-zinc-200 shadow-2xl flex flex-col overflow-hidden bottom-0 right-0 w-full h-[100dvh] sm:bottom-6 sm:right-6 sm:w-[420px] sm:max-h-[600px] sm:rounded-2xl sm:h-auto" data-testid="sous-chef-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-zinc-900 text-white safe-area-top">
            <div className="flex items-center gap-2">
              <div className="relative">
                <ChefHat size={22} weight="fill" />
                <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full" />
              </div>
              <div>
                <span className="font-semibold text-sm">Sous-Chef IA</span>
                <span className="text-[9px] text-zinc-400 ml-1.5 tracking-wide">ELITE</span>
                <span className="text-[9px] text-purple-400 ml-1 tracking-wide flex items-center gap-0.5"><Brain size={9} weight="fill" /></span>
              </div>
            </div>
            <div className="flex items-center gap-0.5 sm:gap-1">
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-7 sm:w-7 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg" onClick={() => setShowTemplateConfig(true)} data-testid="template-config-btn" title="Modelos de Exportação">
                <FileXls size={18} className="sm:hidden" /><FileXls size={16} className="hidden sm:block" />
              </Button>
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-7 sm:w-7 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg" onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }} data-testid="history-btn">
                <ClockCounterClockwise size={18} className="sm:hidden" /><ClockCounterClockwise size={16} className="hidden sm:block" />
              </Button>
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-7 sm:w-7 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg" onClick={startNewConversation} data-testid="new-chat-btn">
                <Plus size={18} className="sm:hidden" /><Plus size={16} className="hidden sm:block" />
              </Button>
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-7 sm:w-7 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg" onClick={() => setIsOpen(false)} data-testid="close-panel-btn">
                <X size={18} className="sm:hidden" /><X size={16} className="hidden sm:block" />
              </Button>
            </div>
          </div>

          {/* History Panel */}
          {showHistory && (
            <div className="flex-1 overflow-y-auto border-b border-zinc-100 bg-zinc-50 max-h-[400px]">
              <div className="p-3 space-y-1">
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Conversas Anteriores</p>
                {historyLoading ? (
                  <div className="text-center py-4"><SpinnerGap size={20} className="animate-spin text-zinc-300 mx-auto" /></div>
                ) : history.length === 0 ? (
                  <p className="text-xs text-zinc-400 text-center py-4">Nenhuma conversa salva</p>
                ) : (
                  history.map(conv => (
                    <div key={conv.conversation_id} className="flex items-center gap-2 p-2 rounded hover:bg-zinc-100 cursor-pointer group" onClick={() => loadConversation(conv.conversation_id)}>
                      <ChatCircleDots size={14} className="text-zinc-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-700 truncate">{conv.title}</p>
                        <p className="text-[10px] text-zinc-400">{new Date(conv.updated_at).toLocaleDateString("pt-BR")}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 text-rose-400" onClick={(e) => { e.stopPropagation(); deleteConversation(conv.conversation_id); }}>
                        <Trash size={10} />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Messages */}
          {!showHistory && (
            <div className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-3 min-h-[200px] sm:min-h-[300px] sm:max-h-[400px]">
              {messages.length === 0 && (
                <div className="space-y-2">
                  <div className="text-center mb-3">
                    <p className="text-xs text-zinc-500 font-medium">Sous-Chef IA — Especialista Elite</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">Ciência dos alimentos, engenharia de cardápio e operações culinárias</p>
                  </div>
                  {/* Price search quick action */}
                  <button className="w-full text-left text-xs p-2.5 rounded-lg border border-blue-100 hover:border-blue-300 hover:bg-blue-50 text-blue-700 transition-colors flex items-center gap-2"
                    onClick={handleSearchPrices} disabled={priceSearching} data-testid="price-search-btn">
                    <CurrencyCircleDollar size={16} weight="fill" />
                    {priceSearching ? "Pesquisando preços..." : "Pesquisar preços dos ingredientes na internet"}
                  </button>
                  {suggestions.map((s, i) => (
                    <button key={`sugg-${i}-${s.slice(0, 20)}`} className="w-full text-left text-xs p-2.5 rounded-lg border border-zinc-100 hover:border-zinc-300 hover:bg-zinc-50 text-zinc-600 transition-colors" onClick={() => handleSend(s)} data-testid={`suggestion-${i}`}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
              {messages.map((msg) => {
                const recipe = msg.role === "assistant" ? parseRecipeBlock(msg.content) : null;
                const checklist = msg.role === "assistant" ? parseChecklistBlock(msg.content) : null;
                const priceUpdate = msg.role === "assistant" ? parsePriceUpdateBlock(msg.content) : null;
                const newIngredient = msg.role === "assistant" ? parseNewIngredientBlock(msg.content) : null;
                const newIngredients = msg.role === "assistant" ? parseAllNewIngredientBlocks(msg.content) : null;
                const ingUpdate = msg.role === "assistant" ? parseIngredientUpdateBlock(msg.content) : null;
                const ingMerge = msg.role === "assistant" ? parseIngredientMergeBlock(msg.content) : null;
                const ingDelete = msg.role === "assistant" ? parseIngredientDeleteBlock(msg.content) : null;
                const exportReq = msg.role === "assistant" ? parseExportBlock(msg.content) : null;
                const isDraft = msg.role === "assistant" && isDraftMessage(msg.content);

                return (
                  <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${msg.role === "user" ? "bg-zinc-900 text-white rounded-br-sm" : "bg-zinc-100 text-zinc-800 rounded-bl-sm"}`}>
                      {msg.content === "..." ? (
                        <div className="flex items-center gap-2">
                          <SpinnerGap size={14} className="animate-spin" />
                          <span className="text-xs text-zinc-400">Analisando contexto...</span>
                        </div>
                      ) : (
                        <>
                          {isDraft && <DraftBadge />}
                          <div className="prose-sous-chef text-xs leading-relaxed" dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />

                          {/* Retry button on connection errors */}
                          {msg.retryText && (
                            <Button size="sm" variant="outline" className="mt-2 w-full text-[11px] h-7 border-zinc-300" onClick={() => handleSend(msg.retryText)} disabled={isLoading} data-testid="chat-retry-btn">
                              <ArrowRight size={12} className="mr-1" /> Tentar novamente
                            </Button>
                          )}

                          {/* Draft approval button */}
                          {isDraft && !recipe && (
                            <div className="mt-2 flex gap-1.5">
                              <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] h-7" onClick={handleApproveDraft} disabled={isLoading} data-testid="approve-draft-btn">
                                <Check size={12} className="mr-1" weight="bold" /> Aprovar Rascunho
                              </Button>
                              <Button size="sm" variant="outline" className="text-[11px] h-7 text-zinc-500" onClick={() => handleSend("Ajuste o rascunho: ")} disabled={isLoading} data-testid="adjust-draft-btn">
                                Pedir Ajuste
                              </Button>
                            </div>
                          )}

                          {/* Recipe apply button (after approval) */}
                          {recipe && (
                            <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <FileText size={12} className="text-emerald-700" weight="fill" />
                                <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider">Ficha Pronta para Registro</span>
                              </div>
                              <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] h-7" onClick={() => handleApplyRecipe(recipe)} disabled={applying} data-testid="apply-recipe-btn">
                                {applying ? <SpinnerGap size={14} className="animate-spin mr-1" /> : <ArrowRight size={14} className="mr-1" />}
                                {applying ? "Registrando..." : `Criar Ficha: "${recipe.name}"`}
                              </Button>
                            </div>
                          )}

                          {/* Action buttons */}
                          {msg.role === "assistant" && msg.actions?.length > 0 && msg.actions.map((action, aIdx) => (
                            <Button key={`act-${msg.id}-${aIdx}`} size="sm" className="mt-1 w-full bg-blue-600 hover:bg-blue-700 text-white text-xs h-7" onClick={() => handleExecuteAction(action)} data-testid={`action-btn-${aIdx}`}>
                              <Lightning size={12} className="mr-1" /> {action.label || "Executar"}
                            </Button>
                          ))}

                          {/* Checklist create button */}
                          {checklist && (
                            <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <CheckSquare size={12} className="text-emerald-700" weight="fill" />
                                <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider">Checklist: {checklist.name}</span>
                              </div>
                              <p className="text-[10px] text-emerald-600 mb-1.5">{checklist.items?.length || 0} itens</p>
                              <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] h-7" onClick={() => handleCreateChecklist(checklist)} data-testid="create-checklist-from-chat-btn">
                                <CheckSquare size={14} className="mr-1" /> Criar Checklist
                              </Button>
                            </div>
                          )}

                          {/* Price update button */}
                          {priceUpdate && (
                            <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-2">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <CurrencyCircleDollar size={12} className="text-blue-700" weight="fill" />
                                <span className="text-[10px] font-bold text-blue-800 uppercase tracking-wider">Atualizar Precos</span>
                              </div>
                              <p className="text-[10px] text-blue-600 mb-1.5">{priceUpdate.ingredients?.length || 0} ingrediente(s) · Fonte: {priceUpdate.source}</p>
                              <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700 text-white text-[11px] h-7" onClick={() => handleApplyPriceUpdate(priceUpdate)} data-testid="apply-price-update-btn">
                                <CurrencyCircleDollar size={14} className="mr-1" /> Aplicar Precos
                              </Button>
                            </div>
                          )}

                          {/* New ingredient(s) button - supports batch creation */}
                          {newIngredients && newIngredients.length > 0 && (
                            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <Package size={12} className="text-amber-700" weight="fill" />
                                <span className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">
                                  {newIngredients.length === 1 ? 'Novo Ingrediente' : `${newIngredients.length} Novos Ingredientes`}
                                </span>
                              </div>
                              <div className="space-y-1 mb-1.5">
                                {newIngredients.map((ni, idx) => (
                                  <p key={`ni-${ni.name}-${idx}`} className="text-[10px] text-amber-700 flex items-center gap-1">
                                    <span className="w-1 h-1 rounded-full bg-amber-400 flex-shrink-0" />
                                    <span className="font-medium">{ni.name}</span>
                                    <span className="text-amber-500">· {ni.quantity_purchased || 1} {ni.unit || 'kg'} · {ni.purchase_price?.toFixed?.(2) || '0.00'}</span>
                                    {ni.category && <span className="text-amber-400 text-[9px]">({ni.category})</span>}
                                  </p>
                                ))}
                              </div>
                              <Button size="sm" className="w-full bg-amber-600 hover:bg-amber-700 text-white text-[11px] h-7" onClick={() => handleCreateNewIngredient(newIngredients)} data-testid="create-ingredient-from-chat-btn">
                                <Package size={14} className="mr-1" /> {newIngredients.length === 1 ? 'Cadastrar Ingrediente' : `Cadastrar ${newIngredients.length} Ingredientes`}
                              </Button>
                            </div>
                          )}
                          {/* Single new ingredient fallback */}
                          {!newIngredients && newIngredient && (
                            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <Package size={12} className="text-amber-700" weight="fill" />
                                <span className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">Novo Ingrediente</span>
                              </div>
                              <p className="text-[10px] text-amber-600 mb-1.5">{newIngredient.name} · {newIngredient.quantity_purchased || 1} {newIngredient.unit || 'kg'} · {newIngredient.purchase_price?.toFixed?.(2) || '0.00'}</p>
                              <Button size="sm" className="w-full bg-amber-600 hover:bg-amber-700 text-white text-[11px] h-7" onClick={() => handleCreateNewIngredient(newIngredient)} data-testid="create-ingredient-from-chat-btn">
                                <Package size={14} className="mr-1" /> Cadastrar Ingrediente
                              </Button>
                            </div>
                          )}

                          {/* Ingredient batch update (categorize, rename, edit) */}
                          {ingUpdate && (
                            <div className="mt-2 bg-violet-50 border border-violet-200 rounded-lg p-2">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <Gear size={12} className="text-violet-700" weight="fill" />
                                <span className="text-[10px] font-bold text-violet-800 uppercase tracking-wider">
                                  Atualizar {ingUpdate.updates?.length || 0} Ingrediente(s)
                                </span>
                              </div>
                              <div className="space-y-0.5 mb-1.5 max-h-[100px] overflow-y-auto">
                                {ingUpdate.updates?.slice(0, 10).map((u, i) => (
                                  <p key={`iu-${u.name}-${i}`} className="text-[10px] text-violet-700 flex items-center gap-1">
                                    <span className="w-1 h-1 rounded-full bg-violet-400 flex-shrink-0" />
                                    <span className="font-medium">{u.name}</span>
                                    <span className="text-violet-500">→ {Object.entries(u.fields || {}).map(([k,v]) => `${k}: ${v}`).join(", ")}</span>
                                  </p>
                                ))}
                                {(ingUpdate.updates?.length || 0) > 10 && <p className="text-[9px] text-violet-400">+{ingUpdate.updates.length - 10} mais...</p>}
                              </div>
                              <Button size="sm" className="w-full bg-violet-600 hover:bg-violet-700 text-white text-[11px] h-7" onClick={() => handleIngredientUpdate(ingUpdate)} data-testid="apply-ingredient-update-btn">
                                <Gear size={14} className="mr-1" /> Aplicar Atualizações
                              </Button>
                            </div>
                          )}

                          {/* Ingredient merge (deduplicate) */}
                          {ingMerge && (
                            <div className="mt-2 bg-orange-50 border border-orange-200 rounded-lg p-2">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <Lightning size={12} className="text-orange-700" weight="fill" />
                                <span className="text-[10px] font-bold text-orange-800 uppercase tracking-wider">
                                  Mesclar {ingMerge.merges?.length || 0} Duplicata(s)
                                </span>
                              </div>
                              <div className="space-y-0.5 mb-1.5 max-h-[100px] overflow-y-auto">
                                {ingMerge.merges?.map((m, i) => (
                                  <p key={`im-${m.keep}-${m.remove}-${i}`} className="text-[10px] text-orange-700 flex items-center gap-1">
                                    <span className="w-1 h-1 rounded-full bg-orange-400 flex-shrink-0" />
                                    <span className="line-through text-orange-400">{m.remove}</span>
                                    <span>→</span>
                                    <span className="font-medium">{m.keep}</span>
                                  </p>
                                ))}
                              </div>
                              <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-700 text-white text-[11px] h-7" onClick={() => handleIngredientMerge(ingMerge)} data-testid="apply-ingredient-merge-btn">
                                <Lightning size={14} className="mr-1" /> Eliminar Duplicatas
                              </Button>
                            </div>
                          )}

                          {/* Ingredient delete */}
                          {ingDelete && (
                            <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <Trash size={12} className="text-red-700" weight="fill" />
                                <span className="text-[10px] font-bold text-red-800 uppercase tracking-wider">
                                  Deletar {ingDelete.names?.length || 0} Ingrediente(s)
                                </span>
                              </div>
                              <div className="space-y-0.5 mb-1.5">
                                {ingDelete.names?.map((n, i) => (
                                  <p key={`id-${n}-${i}`} className="text-[10px] text-red-600">{n}</p>
                                ))}
                              </div>
                              <Button size="sm" className="w-full bg-red-600 hover:bg-red-700 text-white text-[11px] h-7" onClick={() => handleIngredientDelete(ingDelete)} data-testid="apply-ingredient-delete-btn">
                                <Trash size={14} className="mr-1" /> Confirmar Exclusão
                              </Button>
                            </div>
                          )}

                          {/* Export download block */}
                          {exportReq && (
                            <div className="mt-2 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                              <div className="flex items-center gap-2 mb-2">
                                <FileXls size={16} className="text-emerald-600" />
                                <span className="text-[11px] font-semibold text-emerald-800">Exportar para Excel</span>
                              </div>
                              <p className="text-[10px] text-emerald-600 mb-2">
                                {exportReq.description || `Tipo: ${exportReq.type || "lista_compras"}`}
                              </p>
                              <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] h-7" onClick={() => handleExportDownload(exportReq)} data-testid="export-download-btn">
                                <Download size={14} className="mr-1" /> Baixar Excel
                              </Button>
                            </div>
                          )}

                          {/* Navigation links */}
                          {msg.role === "assistant" && msg.content.includes("](/") && (
                            <div className="mt-1">
                              {[...msg.content.matchAll(/\[([^\]]+)\]\(([^)]+)\)/g)].map(([, label, path], i) => (
                                <button key={`nav-${msg.id}-${path}-${i}`} className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 mt-0.5" onClick={() => navigate(path)}>
                                  <ArrowRight size={10} /> {label}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Feedback buttons */}
                          {msg.role === "assistant" && msg.content !== "..." && !feedbackGiven[msg.id] && (
                            <div className="flex items-center gap-1 mt-1.5 pt-1 border-t border-zinc-200/50">
                              <button onClick={() => handleFeedback(msg.id, messages.filter(m => m.role === "assistant").indexOf(msg), "positive")}
                                className="text-zinc-300 hover:text-emerald-500 p-0.5 transition-colors" data-testid={`feedback-up-${msg.id}`}>
                                <ThumbsUp size={12} />
                              </button>
                              <button onClick={() => handleFeedback(msg.id, messages.filter(m => m.role === "assistant").indexOf(msg), "negative")}
                                className="text-zinc-300 hover:text-rose-500 p-0.5 transition-colors" data-testid={`feedback-down-${msg.id}`}>
                                <ThumbsDown size={12} />
                              </button>
                            </div>
                          )}
                          {feedbackGiven[msg.id] && (
                            <div className="flex items-center gap-1 mt-1 text-[10px] text-zinc-400">
                              {feedbackGiven[msg.id] === "positive" ? <ThumbsUp size={10} className="text-emerald-500" weight="fill" /> : <ThumbsDown size={10} className="text-rose-400" weight="fill" />}
                              <span>{feedbackGiven[msg.id] === "positive" ? "Obrigado!" : "Vou melhorar!"}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input */}
          {!showHistory && (
            <div className="p-3 sm:p-3 pb-4 sm:pb-3 border-t border-zinc-100 bg-white safe-area-bottom">
              {isRecording && (
                <div className="flex items-center justify-between mb-2 px-2 py-1.5 bg-rose-50 rounded-lg border border-rose-200">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                    <span className="text-xs text-rose-600 font-medium">Gravando... {recordingTime}s</span>
                  </div>
                  <Button size="sm" variant="ghost" className="h-6 text-rose-600 text-xs" onClick={stopRecording}>
                    <Stop size={12} className="mr-1" /> Parar
                  </Button>
                </div>
              )}
              <div className="flex gap-2 items-end">
                <Button variant="ghost" size="icon" className={`h-11 w-11 sm:h-9 sm:w-9 flex-shrink-0 rounded-xl ${isRecording ? "text-rose-500" : "text-zinc-400 hover:text-zinc-600"}`} onClick={isRecording ? stopRecording : startRecording} disabled={isLoading} data-testid="voice-btn">
                  {isRecording ? <Stop size={20} weight="fill" /> : <Microphone size={20} />}
                </Button>
                <Input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSend()} placeholder="Fale com o Sous-Chef..." className="text-base sm:text-sm h-11 sm:h-9 rounded-xl" disabled={isLoading} data-testid="sous-chef-input" />
                <Button size="icon" className="h-11 w-11 sm:h-9 sm:w-9 bg-zinc-900 hover:bg-zinc-800 flex-shrink-0 rounded-xl" onClick={() => handleSend()} disabled={!input.trim() || isLoading} data-testid="sous-chef-send">
                  <PaperPlaneTilt size={18} weight="fill" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <ExportTemplateConfig open={showTemplateConfig} onClose={() => setShowTemplateConfig(false)} />
    </>
  );
}
